// src/components/teacher/TeacherProfile.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import {
  UserIcon,
  SaveIcon,
  LogoutIcon,
  EditIcon,
  UploadIcon,
  DeleteIcon,
} from "../shared/icons/Icons";
import { USE_DUMMY_DATA, DUMMY_TEACHER } from "./dummyData";

// ── Document upload component ──────────────────────────────────────────────
const DocumentUpload = ({ documents = [], onUpload, onDelete, isEditing, isUploading }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentName, setDocumentName] = useState("");
  const [documentType, setDocumentType] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Auto-populate document name from filename
      if (!documentName) {
        setDocumentName(file.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };

  const handleUpload = () => {
    if (selectedFile && documentName) {
      onUpload({
        file: selectedFile,
        name: documentName,
        type: documentType || "Other",
      });
      setSelectedFile(null);
      setDocumentName("");
      setDocumentType("");
      // Reset file input
      const fileInput = document.getElementById("doc-upload-input");
      if (fileInput) fileInput.value = "";
    }
  };

  const getFileIcon = (fileType) => {
    const type = fileType?.toLowerCase() || "";
    if (type.includes("pdf")) return "📄";
    if (type.includes("image")) return "🖼️";
    if (type.includes("doc")) return "📝";
    if (type.includes("sheet") || type.includes("excel")) return "📊";
    return "📎";
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <div style={{ marginTop: "20px", borderTop: "1px solid #e2e8f0", paddingTop: "20px" }}>
      <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", fontWeight: 700, color: "#1a202c" }}>
        📎 Documents
      </h4>

      {/* Upload area */}
      {isEditing && (
        <div
          style={{
            background: "#f8f9fc",
            borderRadius: "8px",
            padding: "16px",
            border: "1px dashed #cbd5e1",
            marginBottom: "16px",
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "10px", alignItems: "end" }}>
            <div className="tp-field" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", display: "block", marginBottom: "4px" }}>
                Document Name
              </label>
              <input
                className="tp-input"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder="e.g., CNIC, Degree, Certificate"
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "13px" }}
              />
            </div>
            <div className="tp-field" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", display: "block", marginBottom: "4px" }}>
                Type
              </label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "13px", background: "#fff" }}
              >
                <option value="">Select type...</option>
                <option value="CNIC">CNIC</option>
                <option value="Degree">Degree / Certificate</option>
                <option value="CV">CV / Resume</option>
                <option value="Experience Letter">Experience Letter</option>
                <option value="Contract">Contract</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                id="doc-upload-input"
                type="file"
                onChange={handleFileChange}
                style={{ display: "none" }}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
              />
              <button
                type="button"
                className="tp-btn tp-btn--ghost"
                onClick={() => document.getElementById("doc-upload-input")?.click()}
                style={{ padding: "8px 14px", fontSize: "12px" }}
              >
                📁 Choose File
              </button>
              <button
                type="button"
                className="tp-btn"
                onClick={handleUpload}
                disabled={!selectedFile || !documentName || isUploading}
                style={{ padding: "8px 16px", fontSize: "12px", whiteSpace: "nowrap" }}
              >
                {isUploading ? "⏳" : "⬆ Upload"}
              </button>
            </div>
          </div>
          {selectedFile && (
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "8px" }}>
              Selected: <strong>{selectedFile.name}</strong> ({formatFileSize(selectedFile.size)})
            </div>
          )}
        </div>
      )}

      {/* Document list */}
      {documents.length === 0 ? (
        <div style={{ fontSize: "13px", color: "#94a3b8", padding: "12px 0" }}>
          No documents uploaded yet.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {documents.map((doc, index) => (
            <div
              key={doc.id || index}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px",
                background: "#f8f9fc",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                <span style={{ fontSize: "20px" }}>{getFileIcon(doc.type || doc.file_type)}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "13px", color: "#1a202c" }}>
                    {doc.name || doc.file_name || "Untitled"}
                  </div>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>
                    {doc.type || doc.file_type || "Document"} • {doc.size ? formatFileSize(doc.size) : ""}
                    {doc.uploaded_at && ` • Uploaded: ${new Date(doc.uploaded_at).toLocaleDateString()}`}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                {doc.url && (
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "4px 10px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      background: "#1d4ed8",
                      color: "#fff",
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    View
                  </a>
                )}
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => onDelete(doc.id || index)}
                    style={{
                      padding: "4px 8px",
                      borderRadius: "4px",
                      border: "none",
                      background: "#fee2e2",
                      color: "#dc2626",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                    title="Delete document"
                  >
                    🗑
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main TeacherProfile Component ──────────────────────────────────────────
function TeacherProfile({ teacherData, onLogout }) {
  const navigate = useNavigate();
  const source = teacherData || (USE_DUMMY_DATA ? DUMMY_TEACHER : null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Editable fields only: name, address, subject
  const [formData, setFormData] = useState({
    name: source?.name || "",
    address: source?.address || "",
    subject: source?.subject || source?.subject_specialization || "",
  });

  // Non-editable fields (read-only)
  const readOnlyData = {
    email: source?.email || "",
    employee_id: source?.employee_id || source?.teacher_id || "",
    phone: source?.phone || "",
  };

  useEffect(() => {
    if (!source) return;
    setFormData({
      name: source.name || "",
      address: source.address || "",
      subject: source.subject || source.subject_specialization || "",
    });
  }, [source]);

  // ── Fetch documents ──────────────────────────────────────────────────────
  const fetchDocuments = useCallback(async () => {
    if (USE_DUMMY_DATA) {
      setDocuments([
        { id: 1, name: "CNIC", type: "CNIC", size: 245000, url: "#", uploaded_at: new Date().toISOString() },
        { id: 2, name: "Degree Certificate", type: "Degree", size: 1800000, url: "#", uploaded_at: new Date().toISOString() },
        { id: 3, name: "Experience Letter", type: "Experience Letter", size: 350000, url: "#", uploaded_at: new Date().toISOString() },
      ]);
      return;
    }

    setLoadingDocs(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/teachers/me/documents`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to fetch documents:", err);
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      if (USE_DUMMY_DATA) {
        await new Promise((r) => setTimeout(r, 400));
        localStorage.setItem("als_teacher_name", formData.name || "");
        setSaveSuccess(true);
        setIsEditing(false);
        setTimeout(() => setSaveSuccess(false), 2500);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/teachers/me`, {
        method: "PUT",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          address: formData.address,
          subject_specialization: formData.subject,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update profile");
      }

      const data = await response.json();
      localStorage.setItem("als_teacher_name", data.name || "");
      setSaveSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to update profile:", err);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Document handlers ────────────────────────────────────────────────────
  const handleUploadDocument = async ({ file, name, type }) => {
    if (USE_DUMMY_DATA) {
      setIsUploading(true);
      await new Promise((r) => setTimeout(r, 500));
      const newDoc = {
        id: Date.now(),
        name: name,
        type: type || "Other",
        size: file.size,
        url: "#",
        uploaded_at: new Date().toISOString(),
      };
      setDocuments((prev) => [...prev, newDoc]);
      setIsUploading(false);
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", name);
      formData.append("type", type || "Other");

      const response = await fetch(`${API_BASE_URL}/api/teachers/me/documents`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload document");
      }

      const data = await response.json();
      setDocuments((prev) => [...prev, data]);
    } catch (err) {
      console.error("Failed to upload document:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (USE_DUMMY_DATA) {
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/teachers/me/documents/${docId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error("Failed to delete document");
      }

      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (err) {
      console.error("Failed to delete document:", err);
    }
  };

  const initials = (formData.name || "T")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div>
      <div className="tp-page-header">
        <div>
          <h2>
            <UserIcon />
            My Profile
          </h2>
          <p>Manage your personal information</p>
        </div>
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          {!isEditing ? (
            <button
              type="button"
              className="tp-btn"
              onClick={() => setIsEditing(true)}
            >
              <EditIcon /> Edit Profile
            </button>
          ) : (
            <>
              <button
                type="button"
                className="tp-btn tp-btn--ghost"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tp-btn"
                onClick={handleSave}
                disabled={isSaving}
              >
                <SaveIcon /> {isSaving ? "Saving..." : "Save"}
              </button>
            </>
          )}
          <button
            type="button"
            className="tp-btn tp-btn--ghost"
            onClick={() => navigate("/teacher/campus")}
          >
            Campus Info
          </button>
        </div>
      </div>

      {saveSuccess ? (
        <div className="tp-toast">Profile updated successfully.</div>
      ) : null}

      <div className="tp-card">
        <div className="tp-profile-hero">
          <div className="tp-avatar">{initials}</div>
          <div>
            <h3 style={{ margin: "0 0 0.25rem", fontSize: "1.25rem" }}>
              {formData.name || "Teacher"}
            </h3>
            <p style={{ margin: 0, color: "#64748b" }}>
              {formData.subject || "Subject not set"} ·{" "}
              {readOnlyData.employee_id || "No employee ID"}
            </p>
          </div>
        </div>

        <div className="tp-form-grid">
          {/* Editable fields */}
          <div className="tp-field">
            <label htmlFor="tp-profile-name">Full Name</label>
            <input
              id="tp-profile-name"
              className="tp-input"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
          </div>

          <div className="tp-field">
            <label htmlFor="tp-profile-subject">Subject / Specialization</label>
            <input
              id="tp-profile-subject"
              className="tp-input"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              disabled={!isEditing}
              placeholder="e.g., Mathematics, English, Science"
            />
          </div>

          <div className="tp-field" style={{ gridColumn: "1 / -1" }}>
            <label htmlFor="tp-profile-address">Address</label>
            <input
              id="tp-profile-address"
              className="tp-input"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              disabled={!isEditing}
              placeholder="Enter your address"
            />
          </div>

          {/* Non-editable / Read-only fields */}
          <div className="tp-field">
            <label htmlFor="tp-profile-email">Email (Read-only)</label>
            <input
              id="tp-profile-email"
              className="tp-input"
              value={readOnlyData.email}
              disabled={true}
              style={{ background: "#f1f5f9", cursor: "not-allowed", opacity: 0.7 }}
            />
          </div>

          <div className="tp-field">
            <label htmlFor="tp-profile-phone">Phone (Read-only)</label>
            <input
              id="tp-profile-phone"
              className="tp-input"
              value={readOnlyData.phone}
              disabled={true}
              style={{ background: "#f1f5f9", cursor: "not-allowed", opacity: 0.7 }}
            />
          </div>

          <div className="tp-field">
            <label htmlFor="tp-profile-employee_id">Employee / Teacher ID (Read-only)</label>
            <input
              id="tp-profile-employee_id"
              className="tp-input"
              value={readOnlyData.employee_id}
              disabled={true}
              style={{ background: "#f1f5f9", cursor: "not-allowed", opacity: 0.7 }}
            />
          </div>
        </div>

        {/* Documents Section */}
        {loadingDocs ? (
          <div style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>
            Loading documents...
          </div>
        ) : (
          <DocumentUpload
            documents={documents}
            onUpload={handleUploadDocument}
            onDelete={handleDeleteDocument}
            isEditing={isEditing}
            isUploading={isUploading}
          />
        )}

        <div
          style={{
            marginTop: "1.75rem",
            paddingTop: "1.25rem",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <button
            type="button"
            className="tp-btn tp-btn--ghost"
            onClick={onLogout}
            style={{ color: "#b91c1c" }}
          >
            <LogoutIcon /> Logout
          </button>
        </div>
      </div>
    </div>
  );
}

export default TeacherProfile;